import React, { useEffect, useState } from 'react';
import {
  CONSENT_CHANGED_EVENT,
  needsConsentPrompt,
  setConsent,
} from '../utils/analytics';

/**
 * Asks for analytics cookie consent. Renders nothing unless GA4 is configured
 * (REACT_APP_GA4_ID), this is the web app, and no choice is stored yet.
 * Visibility is decided after mount so pre-rendered HTML hydrates cleanly.
 */
export const CookieConsentBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const update = () => setVisible(needsConsentPrompt());
    update();
    window.addEventListener(CONSENT_CHANGED_EVENT, update);
    return () => window.removeEventListener(CONSENT_CHANGED_EVENT, update);
  }, []);

  if (!visible) {
    return null;
  }

  const buttonBase =
    'min-w-[6.5rem] rounded-lg px-4 py-2 text-sm font-semibold font-sans transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--clue-green-accent)] focus-visible:ring-offset-2';
  // Bottom padding adds the iOS safe-area inset (home indicator); it is 0 elsewhere.
  // Accept uses green-dark with card-coloured text: 4.5:1+ contrast in light and dark mode.

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 pb-[calc(0.75rem_+_env(safe-area-inset-bottom,0px))] sm:p-4 sm:pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))] motion-safe:animate-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-300"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-xl border border-[var(--clue-border-mid)] bg-[var(--clue-bg-card)] p-4 shadow-lg sm:flex-row sm:items-center sm:gap-4">
        <p className="flex-1 font-sans text-sm text-[var(--clue-text-primary)]">
          We&rsquo;d like to use analytics cookies to see which parts of Wallstreetle people use, so we can
          improve it. We only set them if you accept.{' '}
          <a
            href="/policies/privacy"
            className="text-[var(--clue-text-secondary)] underline hover:text-[var(--clue-text-primary)]"
          >
            Privacy Policy
          </a>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setConsent('denied')}
            className={`${buttonBase} border border-[var(--clue-border-mid)] bg-[var(--clue-bg-secondary)] text-[var(--clue-text-primary)] hover:bg-[var(--clue-bg-primary)]`}
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => setConsent('granted')}
            className={`${buttonBase} bg-[var(--clue-green-dark)] text-[var(--clue-bg-card)] hover:opacity-90`}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
